import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  STEP_ORDER, ONBOARDING_EVENTS, SCAN_EVENTS, INTERACTION_EVENTS,
  fetchEventsByType, formatDuration, parsePayload, normalizeStepId,
  computeUserJourneys,
} from './onboardingAnalyticsHelpers';

const ALL_JOURNEY_EVENT_TYPES = [
  ...ONBOARDING_EVENTS,
  ...SCAN_EVENTS,
  ...INTERACTION_EVENTS,
];

export default function UserJourneyExplorer({ dateRange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allJourneyEvents, setAllJourneyEvents] = useState([]);
  const [selectedActor, setSelectedActor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchJourneyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = new Date(dateRange.from).toISOString();
      const to = new Date(dateRange.to + 'T23:59:59').toISOString();
      const results = await Promise.all(
        ALL_JOURNEY_EVENT_TYPES.map(et => fetchEventsByType(et, from, to))
      );
      setAllJourneyEvents(results.flat());
    } catch (err) {
      setError(err.message || 'Failed to load journey data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchJourneyData(); }, [fetchJourneyData]);

  const journeys = useMemo(() => computeUserJourneys(allJourneyEvents), [allJourneyEvents]);

  const filteredJourneys = useMemo(() => {
    let list = journeys;
    if (statusFilter !== 'all') list = list.filter(j => j.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(j =>
        (j.userId || '').toLowerCase().includes(q) ||
        (j.sessionId || '').toLowerCase().includes(q) ||
        (j.actor || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [journeys, statusFilter, searchQuery]);

  const selectedJourney = useMemo(
    () => journeys.find(j => j.actor === selectedActor) || null,
    [journeys, selectedActor]
  );

  const counts = useMemo(() => {
    const c = { total: journeys.length, completed: 0, abandoned: 0, inProgress: 0 };
    journeys.forEach(j => {
      if (j.status === 'completed') c.completed++;
      else if (j.status === 'abandoned') c.abandoned++;
      else c.inProgress++;
    });
    return c;
  }, [journeys]);

  if (loading) return <div className="loading-spinner">Loading user journeys…</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (journeys.length === 0) {
    return (
      <div className="section-card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: 'var(--text-muted)' }}>No onboarding journeys found for this date range.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          { label: 'All', value: 'all', count: counts.total },
          { label: 'Completed', value: 'completed', count: counts.completed },
          { label: 'Abandoned', value: 'abandoned', count: counts.abandoned },
          { label: 'In Progress', value: 'in-progress', count: counts.inProgress },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => { setStatusFilter(f.value); setSelectedActor(null); }}
            style={{
              padding: '6px 16px', borderRadius: '20px',
              border: statusFilter === f.value ? '2px solid var(--action-primary-bg, #4a90e2)' : '1px solid var(--border-light)',
              background: statusFilter === f.value ? 'var(--action-primary-bg, #4a90e2)' : 'var(--card-bg)',
              color: statusFilter === f.value ? '#fff' : 'var(--text-primary)',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {f.label} ({f.count})
          </button>
        ))}
        <input
          type="text"
          placeholder="Search by user or session ID…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            marginLeft: 'auto', padding: '6px 12px',
            border: '1px solid var(--border-light)', borderRadius: '6px',
            fontSize: '13px', background: 'var(--card-bg)', color: 'var(--text-primary)', minWidth: '200px',
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedJourney ? '380px 1fr' : '1fr', gap: '24px' }}>
        <div className="section-card" style={{ padding: '0', maxHeight: '700px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-subtle, #f5f5f5)', zIndex: 1 }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-light)' }}>User / Session</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-light)' }}>Status</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-light)' }}>Last Step</th>
                <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-light)' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {filteredJourneys.slice(0, 200).map(j => (
                <tr
                  key={j.actor}
                  onClick={() => setSelectedActor(j.actor === selectedActor ? null : j.actor)}
                  style={{ cursor: 'pointer', background: j.actor === selectedActor ? 'var(--surface-hover, #f0f4ff)' : undefined, borderBottom: '1px solid var(--border-light)' }}
                >
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px' }}>
                    {(j.userId || j.sessionId || j.actor).slice(0, 12)}…
                  </td>
                  <td style={{ padding: '8px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                      background: j.status === 'completed' ? '#e8f5e9' : j.status === 'abandoned' ? '#fce4ec' : '#fff3e0',
                      color: j.status === 'completed' ? '#2e7d32' : j.status === 'abandoned' ? '#c62828' : '#e65100',
                    }}>{j.status}</span>
                  </td>
                  <td style={{ padding: '8px', fontSize: '12px' }}>{j.lastStepLabel}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontSize: '12px', color: 'var(--text-muted)' }}>{formatDuration(j.totalDurationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredJourneys.length > 200 && (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px' }}>
              Showing 200 of {filteredJourneys.length} journeys
            </p>
          )}
        </div>

        {selectedJourney && <UserJourneyDetail journey={selectedJourney} navigate={navigate} />}
      </div>
    </div>
  );
}

function UserJourneyDetail({ journey, navigate }) {
  const j = journey;

  const stepTimeline = useMemo(() => {
    const timeline = [];
    let currentStep = null;

    j.events.forEach(ev => {
      const p = parsePayload(ev);
      if (ev.eventType === 'ONBOARDING_STEP_VIEWED') {
        if (currentStep) {
          const entry = timeline.find(t => t.stepId === currentStep && !t.endTime);
          if (entry) entry.endTime = ev.occurredAt;
        }
        currentStep = normalizeStepId(p.entityId || p.label || '');
        timeline.push({
          stepId: currentStep,
          stepLabel: STEP_ORDER.find(s => s.id === currentStep)?.label || currentStep,
          startTime: ev.occurredAt, endTime: null, interactions: [], scanEvents: [],
        });
      }

      const lastStep = timeline.length > 0 ? timeline[timeline.length - 1] : null;
      if (lastStep) {
        if (ev.eventType === 'ONBOARDING_INTERACTION') {
          lastStep.interactions.push({ action: p.action || '?', label: p.label || '', metadata: p.metadata || {}, time: ev.occurredAt });
        } else if (ev.eventType === 'ONBOARDING_SCAN' || ev.eventType === 'ONBOARDING_SCAN_VIDEO') {
          lastStep.scanEvents.push({ type: p.action || 'unknown', metadata: p.metadata || {}, time: ev.occurredAt });
        }
      }
    });

    if (timeline.length > 0 && !timeline[timeline.length - 1].endTime) {
      timeline[timeline.length - 1].endTime = j.events[j.events.length - 1]?.occurredAt;
    }
    timeline.forEach(t => { t.durationMs = t.startTime && t.endTime ? new Date(t.endTime) - new Date(t.startTime) : 0; });
    return timeline;
  }, [j.events]);

  const scanSummary = useMemo(() => {
    const scanEvents = j.events.filter(ev => ev.eventType === 'ONBOARDING_SCAN' || ev.eventType === 'ONBOARDING_SCAN_VIDEO');
    if (!scanEvents.length) return null;
    let scanDurationMs = null, coursesDetected = null;
    const videosWatched = [];
    scanEvents.forEach(ev => {
      const p = parsePayload(ev);
      if (p.action === 'scan_completed') { scanDurationMs = p.metadata?.scanDurationMs; coursesDetected = p.metadata?.coursesDetected; }
      if (p.action === 'video_selected') { videosWatched.push(p.metadata?.videoTitle || 'Unknown'); }
    });
    return { scanDurationMs, coursesDetected, videosWatched, totalScanEvents: scanEvents.length };
  }, [j.events]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Journey Detail</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
              {j.userId && <>User: <code style={{ fontSize: '12px' }}>{j.userId}</code><br /></>}
              Session: <code style={{ fontSize: '12px' }}>{j.sessionId || j.actor}</code>
            </p>
          </div>
          <span style={{
            padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
            background: j.status === 'completed' ? '#e8f5e9' : j.status === 'abandoned' ? '#fce4ec' : '#fff3e0',
            color: j.status === 'completed' ? '#2e7d32' : j.status === 'abandoned' ? '#c62828' : '#e65100',
          }}>{j.status}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Started</div><div style={{ fontSize: '14px', fontWeight: 600 }}>{j.startedAt ? new Date(j.startedAt).toLocaleString() : '—'}</div></div>
          <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Duration</div><div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDuration(j.totalDurationMs)}</div></div>
          <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Steps</div><div style={{ fontSize: '14px', fontWeight: 600 }}>{j.stepsVisited.length} / {STEP_ORDER.length}</div></div>
          <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Events</div><div style={{ fontSize: '14px', fontWeight: 600 }}>{j.eventCount}</div></div>
        </div>

        {j.sessionId && (
          <button onClick={() => navigate(`/settings/session-journey/${j.sessionId}`)}
            style={{ marginTop: '12px', padding: '6px 14px', background: 'transparent', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '12px', color: 'var(--primary, #4a90e2)', cursor: 'pointer' }}>
            View Full Session Journey →
          </button>
        )}
      </div>

      <div className="section-card">
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Step Progress</h3>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {STEP_ORDER.map((s, idx) => {
            const visited = j.stepsVisited.includes(s.id);
            const isCurrent = s.id === j.lastStepReached && j.status !== 'completed';
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
                  background: visited ? (isCurrent ? '#ff9800' : '#4caf50') : 'var(--surface-subtle, #e0e0e0)',
                  color: visited ? '#fff' : 'var(--text-muted)',
                  border: isCurrent ? '2px solid #e65100' : 'none',
                }}>{idx + 1}</div>
                {idx < STEP_ORDER.length - 1 && (
                  <div style={{ width: '20px', height: '2px', background: visited && j.stepsVisited.includes(STEP_ORDER[idx + 1]?.id) ? '#4caf50' : 'var(--border-light)' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {scanSummary && (
        <div className="section-card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>Scan Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', fontSize: '13px' }}>
            <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Scan Duration</div><div style={{ fontWeight: 600 }}>{formatDuration(scanSummary.scanDurationMs)}</div></div>
            <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Courses Found</div><div style={{ fontWeight: 600 }}>{scanSummary.coursesDetected ?? '—'}</div></div>
            <div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>Videos Watched</div><div style={{ fontWeight: 600 }}>{scanSummary.videosWatched.length}</div></div>
          </div>
          {scanSummary.videosWatched.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>Videos: {scanSummary.videosWatched.join(', ')}</div>
          )}
        </div>
      )}

      <div className="section-card" style={{ padding: '16px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Step-by-Step Timeline</h3>
        {stepTimeline.map((step, idx) => <StepTimelineEntry key={idx} step={step} />)}
      </div>
    </div>
  );
}

function StepTimelineEntry({ step }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = step.interactions.length > 0 || step.scanEvents.length > 0;

  return (
    <div style={{ borderLeft: '3px solid var(--action-primary-bg, #4a90e2)', marginBottom: '12px', paddingLeft: '16px' }}>
      <div onClick={() => hasDetail && setExpanded(!expanded)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: hasDetail ? 'pointer' : 'default', padding: '6px 0' }}>
        <div>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{step.stepLabel}</span>
          {hasDetail && <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>{expanded ? '▾' : '▸'} {step.interactions.length + step.scanEvents.length} events</span>}
        </div>
        <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
          <span>{step.startTime ? new Date(step.startTime).toLocaleTimeString() : ''}</span>
          <span>{formatDuration(step.durationMs)}</span>
        </div>
      </div>
      {expanded && (
        <div style={{ paddingTop: '8px', paddingBottom: '4px' }}>
          {step.interactions.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase' }}>Interactions</div>
              {step.interactions.map((i, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 0', fontSize: '12px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)', minWidth: '70px' }}>{new Date(i.time).toLocaleTimeString()}</span>
                  <span style={{ padding: '1px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 600, background: 'var(--surface-subtle, #f0f0f0)', color: 'var(--text-secondary)' }}>{i.action}</span>
                  {i.metadata?.fieldName && <span style={{ color: 'var(--text-muted)' }}>field: {i.metadata.fieldName}</span>}
                  {i.metadata?.destination && <span style={{ color: 'var(--text-muted)' }}>→ {i.metadata.destination}</span>}
                  {i.metadata?.videoTitle && <span style={{ color: 'var(--text-muted)' }}>🎬 {i.metadata.videoTitle}</span>}
                </div>
              ))}
            </div>
          )}
          {step.scanEvents.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase' }}>Scan Activity</div>
              {step.scanEvents.map((se, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 0', fontSize: '12px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)', minWidth: '70px' }}>{new Date(se.time).toLocaleTimeString()}</span>
                  <span style={{
                    padding: '1px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 600,
                    background: se.type === 'scan_completed' ? '#e8f5e9' : se.type === 'scan_error' ? '#fce4ec' : 'var(--surface-subtle, #f0f0f0)',
                    color: se.type === 'scan_completed' ? '#2e7d32' : se.type === 'scan_error' ? '#c62828' : 'var(--text-secondary)',
                  }}>{se.type}</span>
                  {se.metadata?.scanDurationMs && <span style={{ color: 'var(--text-muted)' }}>{formatDuration(se.metadata.scanDurationMs)}</span>}
                  {se.metadata?.videoTitle && <span style={{ color: 'var(--text-muted)' }}>🎬 {se.metadata.videoTitle}</span>}
                  {se.metadata?.coursesDetected != null && <span style={{ color: 'var(--text-muted)' }}>{se.metadata.coursesDetected} courses</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
